import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Badge,
  Tooltip,
  Alert,
  Container,
  useTheme,
  alpha,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Search,
  Clear,
  PersonOutline as PersonIcon,
  CalendarMonth as CalendarIcon,
  Speed as SpeedIcon,
  Restaurant as NutritionIcon,
  AccessibilityNew as PilatesIcon,
  MedicalServices as AlliedHealthIcon,
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Launch as LaunchIcon,
  FitnessCenter as FitnessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { useAuth } from '../auth/useAuth';
import { managedLocations, whereLocation } from '../auth/managedLocations';
import { format, differenceInWeeks, isValid } from 'date-fns';
import CustomLoader from '../../components/common/CustomLoader';
import { toDate } from '../../shared/utils/dateHelpers';

interface FoundationClient {
  id: string;
  client_id: string;
  client_name: string;
  current_programme: string;
  lead_coach_name: string;
  primary_location: string;
  health_consult_date: string | null;
  first_foundation_session_date: string | null;
  first_session_date: string | null;
  foundation_pt_sessions: number;
  foundation_pilates_sessions: number;
  foundation_allied_health_sessions: number;
  foundation_physical_performance_sessions: number;
  foundation_nutrition_sessions: number;
  foundation_plus_pt_sessions: number;
  foundation_plus_pilates_sessions: number;
  foundation_plus_physical_performance_sessions: number;
  foundation_plus_nutrition_sessions: number;
  foundation_expiration_date: string | null;
  foundation_plus_expiration_date: string | null;
  visit_history?: any[];
}

const parseFoundationProgrammeType = (programme: string): { type: string; number: string; isPlus: boolean } => {
  const isPlus = programme.includes('+');
  const programmeUpper = programme.toUpperCase();
  
  let type = 'Regular';
  let number = '';
  
  if (programmeUpper.includes('MAX')) {
    type = 'Max';
    const match = programmeUpper.match(/MAX\s*(\d+)/);
    if (match) {
      number = match[1];
    }
  } else if (programmeUpper.includes('PROMO')) {
    type = 'Promo';
    const match = programmeUpper.match(/PROMO\s*(\d+)/);
    if (match) {
      number = match[1];
    }
  } else {
    const match = programmeUpper.match(/FOUNDATION.*?(\d+)/);
    if (match) {
      number = match[1];
    }
  }
  
  return { type, number, isPlus };
};

const getProgrammeChipColor = (type: string, isPlus: boolean): 'primary' | 'secondary' | 'default' | 'success' => {
  if (isPlus) {
    return type === 'Max' ? 'success' : 'secondary';
  }
  if (type === 'Max') return 'primary';
  if (type === 'Promo') return 'secondary';
  return 'default';
};

const findFirstFoundationSession = (visitHistory: any[]): string | null => {
  if (!visitHistory || visitHistory.length === 0) return null;
  
  const foundationSessions = visitHistory
    .filter(visit => {
      const pricingOption = visit.pricing_option?.toLowerCase() || '';
      return pricingOption.includes('foundation');
    })
    .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime());
  
  return foundationSessions.length > 0 ? foundationSessions[0].session_date : null;
};

// Normalize client name from "Lastname, Firstname" to "Firstname Lastname"
const normalizeClientName = (name: string): string => {
  if (!name) return '';
  if (name.includes(',')) {
    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return `${firstName} ${lastName}`;
  }
  return name;
};

export const FoundationOverview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, userMetadata } = useAuth();
  const [clients, setClients] = useState<FoundationClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [programmeTypeFilter, setProgrammeTypeFilter] = useState<string>('');
  const [programmeFilter, setProgrammeFilter] = useState<string>('all');

  const uniqueLocations = useMemo(() => {
    const locations = new Set(clients.map(c => c.primary_location).filter(Boolean));
    return Array.from(locations).sort();
  }, [clients]);

  const uniqueCoaches = useMemo(() => {
    const coaches = new Set(clients.map(c => c.lead_coach_name).filter(Boolean));
    return Array.from(coaches).sort();
  }, [clients]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (locationFilter) count++;
    if (coachFilter) count++;
    if (programmeTypeFilter) count++;
    if (programmeFilter !== 'all') count++;
    return count;
  }, [searchTerm, locationFilter, coachFilter, programmeTypeFilter, programmeFilter]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const now = new Date();
    let activeClients = 0;
    let inactiveClients = 0;
    let foundationCount = 0;
    let foundationPlusCount = 0;

    clients.forEach(client => {
      const { isPlus } = parseFoundationProgrammeType(client.current_programme);
      const expirationDate = isPlus ? client.foundation_plus_expiration_date : client.foundation_expiration_date;
      
      if (isPlus) {
        foundationPlusCount++;
      } else {
        foundationCount++;
      }
      
      if (expirationDate) {
        const expDate = toDate(expirationDate);
        if (expDate && isValid(expDate)) {
          if (expDate >= now) {
            activeClients++;
          } else {
            inactiveClients++;
          }
        }
      }
    });

    return {
      total: clients.length,
      active: activeClients,
      inactive: inactiveClients,
      foundation: foundationCount,
      foundationPlus: foundationPlusCount
    };
  }, [clients]);

  useEffect(() => {
    const fetchFoundationClients = async () => {
      if (!user || !userMetadata) return;

      setLoading(true);
      setError(null);

      try {
        const userRole = userMetadata.role;
        
        if (userRole !== 'ADMIN' && userRole !== 'MANAGER' && userRole !== 'USER') {
          setError('You do not have permission to view this page.');
          setLoading(false);
          return;
        }

        let q;
        if (userRole === 'ADMIN') {
          q = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            where('current_programme', '>=', 'Foundation'),
            where('current_programme', '<=', 'Foundation\uf8ff'),
            orderBy('current_programme'),
            orderBy('client_name')
          );
        } else if (userRole === 'MANAGER' && managedLocations(userMetadata).length > 0) {
          q = query(
            collection(db, 'bigquery_cache/client_data/comprehensive_details'),
            where('current_programme', '>=', 'Foundation'),
            where('current_programme', '<=', 'Foundation\uf8ff'),
            whereLocation('primary_location', managedLocations(userMetadata)),
            orderBy('current_programme'),
            orderBy('client_name')
          );
        } else if (userRole === 'USER') {
          setClients([]);
          setLoading(false);
          return;
        } else {
          setError('Invalid role or missing location assignment.');
          setLoading(false);
          return;
        }

        const querySnapshot = await getDocs(q);
        const foundationClients: FoundationClient[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const programme = data.current_programme?.toLowerCase() || '';
          
          if (programme.includes('foundation')) {
            const firstFoundationSession = data.first_foundation_session_date || 
              findFirstFoundationSession(data.visit_history || []);
            
            foundationClients.push({
              id: doc.id,
              client_id: data.client_id || '',
              client_name: data.client_name || '',
              current_programme: data.current_programme || '',
              lead_coach_name: data.lead_coach_name || '',
              primary_location: data.primary_location || '',
              health_consult_date: data.health_consult_date || null,
              first_foundation_session_date: firstFoundationSession,
              first_session_date: data.first_session_date || null,
              foundation_pt_sessions: data.foundation_pt_sessions || 0,
              foundation_pilates_sessions: data.foundation_pilates_sessions || 0,
              foundation_allied_health_sessions: data.foundation_allied_health_sessions || 0,
              foundation_physical_performance_sessions: data.foundation_physical_performance_sessions || 0,
              foundation_nutrition_sessions: data.foundation_nutrition_sessions || 0,
              foundation_plus_pt_sessions: data.foundation_plus_pt_sessions || 0,
              foundation_plus_pilates_sessions: data.foundation_plus_pilates_sessions || 0,
              foundation_plus_physical_performance_sessions: data.foundation_plus_physical_performance_sessions || 0,
              foundation_plus_nutrition_sessions: data.foundation_plus_nutrition_sessions || 0,
              foundation_expiration_date: data.foundation_expiration_date || null,
              foundation_plus_expiration_date: data.foundation_plus_expiration_date || null,
              visit_history: data.visit_history || [],
            });
          }
        });

        setClients(foundationClients);
      } catch (err) {
        console.error('Error fetching Foundation clients:', err);
        setError('Failed to load Foundation clients. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFoundationClients();
  }, [user, userMetadata]);

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = client.client_name.toLowerCase().includes(searchLower);
      const idMatch = client.client_id.toLowerCase().includes(searchLower);
      const locationMatch = !locationFilter || client.primary_location === locationFilter;
      const coachMatch = !coachFilter || client.lead_coach_name === coachFilter;
      
      const { type, isPlus } = parseFoundationProgrammeType(client.current_programme);
      const typeMatch = !programmeTypeFilter || type === programmeTypeFilter;
      
      const programmeMatch = programmeFilter === 'all' || 
        (programmeFilter === 'foundation' && !isPlus) ||
        (programmeFilter === 'foundation_plus' && isPlus);
      
      return (nameMatch || idMatch) && locationMatch && coachMatch && typeMatch && programmeMatch;
    });
  }, [clients, searchTerm, locationFilter, coachFilter, programmeTypeFilter, programmeFilter]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setLocationFilter('');
    setCoachFilter('');
    setProgrammeTypeFilter('');
    setProgrammeFilter('all');
  };

  const columns: GridColDef[] = [
    {
      field: 'client',
      headerName: 'Client',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const normalizedName = normalizeClientName(params.row.client_name);
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {normalizedName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.client_id}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'lead_coach_name',
      headerName: 'Coach',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value || 'Not assigned'}</Typography>
        </Box>
      ),
    },
    {
      field: 'current_programme',
      headerName: 'Programme',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const { type, number, isPlus } = parseFoundationProgrammeType(params.value);
        const label = isPlus ? `${type}+ ${number}` : `${type} ${number}`;
        return (
          <Box display="flex" gap={1} alignItems="center">
            <Chip 
              label={label.trim()} 
              size="small" 
              color={getProgrammeChipColor(type, isPlus)}
              variant={isPlus ? "filled" : "outlined"}
            />
            {isPlus && (
              <Typography variant="caption" color="primary" fontWeight="bold">
                Foundation+
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'health_consult_date',
      headerName: 'Health Consult',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
        const date = toDate(params.value);
        if (!date || !isValid(date)) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2">{format(date, 'dd/MM/yyyy')}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'first_session',
      headerName: 'First Session',
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const firstSession = params.row.first_foundation_session_date || params.row.first_session_date;
        if (!firstSession) return <Typography variant="body2" color="text.secondary">-</Typography>;
        
        const date = toDate(firstSession);
        if (!date || !isValid(date)) return <Typography variant="body2" color="text.secondary">-</Typography>;
        
        const weeksAgo = differenceInWeeks(new Date(), date);
        return (
          <Box>
            <Typography variant="body2">{format(date, 'dd/MM/yyyy')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {weeksAgo} weeks ago
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'sessions',
      headerName: 'Sessions',
      flex: 2,
      minWidth: 320,
      renderCell: (params: GridRenderCellParams) => {
        const { isPlus } = parseFoundationProgrammeType(params.row.current_programme);
        
        const sessions = isPlus ? [
          { count: params.row.foundation_plus_pt_sessions, icon: <FitnessIcon fontSize="small" />, label: 'PT' },
          { count: params.row.foundation_plus_pilates_sessions, icon: <PilatesIcon fontSize="small" />, label: 'Pilates' },
          { count: params.row.foundation_plus_physical_performance_sessions, icon: <SpeedIcon fontSize="small" />, label: 'VALD' },
          { count: params.row.foundation_plus_nutrition_sessions, icon: <NutritionIcon fontSize="small" />, label: 'Nutrition' },
        ] : [
          { count: params.row.foundation_pt_sessions, icon: <FitnessIcon fontSize="small" />, label: 'PT' },
          { count: params.row.foundation_pilates_sessions, icon: <PilatesIcon fontSize="small" />, label: 'Pilates' },
          { count: params.row.foundation_allied_health_sessions, icon: <AlliedHealthIcon fontSize="small" />, label: 'Allied Health' },
          { count: params.row.foundation_physical_performance_sessions, icon: <SpeedIcon fontSize="small" />, label: 'VALD' },
          { count: params.row.foundation_nutrition_sessions, icon: <NutritionIcon fontSize="small" />, label: 'Nutrition' },
        ];
        
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {sessions.map((session, index) => (
              <Tooltip key={index} title={session.label}>
                <Chip
                  icon={session.icon}
                  label={session.count}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    minWidth: 50,
                    bgcolor: isPlus ? alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                    borderColor: isPlus ? theme.palette.secondary.main : theme.palette.divider,
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'expiration',
      headerName: 'Expiration',
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const { isPlus } = parseFoundationProgrammeType(params.row.current_programme);
        const expirationDate = isPlus ? params.row.foundation_plus_expiration_date : params.row.foundation_expiration_date;
        
        if (!expirationDate) return <Typography variant="body2" color="text.secondary">-</Typography>;
        
        const date = toDate(expirationDate);
        if (!date || !isValid(date)) return <Typography variant="body2" color="text.secondary">-</Typography>;
        
        const weeksLeft = differenceInWeeks(date, new Date());
        const isExpired = weeksLeft < 0;
        const isExpiringSoon = weeksLeft >= 0 && weeksLeft < 4;
        
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Box>
              <Typography variant="body2">{format(date, 'dd/MM/yyyy')}</Typography>
              <Typography 
                variant="caption" 
                color={isExpired ? 'error' : isExpiringSoon ? 'warning.main' : 'text.secondary'}
              >
                {isExpired ? 'Expired' : `${weeksLeft} weeks left`}
              </Typography>
            </Box>
            {isExpired ? (
              <WarningIcon color="error" fontSize="small" />
            ) : isExpiringSoon ? (
              <TimerIcon color="warning" fontSize="small" />
            ) : (
              <CheckIcon color="success" fontSize="small" />
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={() => navigate(`/clients/${params.row.id}`)}
          sx={{ textTransform: 'none' }}
        >
          View
        </Button>
      ),
    },
  ];

  if (userMetadata && userMetadata.role !== 'ADMIN' && userMetadata.role !== 'MANAGER' && userMetadata.role !== 'USER') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
          Foundation Overview
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Track and manage all Foundation and Foundation+ programme clients
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                        Active Clients
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.success.main, mt: 1 }}>
                        {statistics.active}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                  }}
                />
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                        Inactive Clients
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.error.main, mt: 1 }}>
                        {statistics.inactive}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                  }}
                />
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                        Foundation
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mt: 1 }}>
                        {statistics.foundation}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FitnessIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                />
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                        Foundation+
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.secondary.main, mt: 1 }}>
                        {statistics.foundationPlus}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <GroupsIcon sx={{ color: theme.palette.secondary.main, fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  }}
                />
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Programme</InputLabel>
                    <Select
                      value={programmeFilter}
                      onChange={(e) => setProgrammeFilter(e.target.value)}
                      label="Programme"
                    >
                      <MenuItem value="all">All Programmes</MenuItem>
                      <MenuItem value="foundation">Foundation</MenuItem>
                      <MenuItem value="foundation_plus">Foundation+</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      label="Location"
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {uniqueLocations.map(location => (
                        <MenuItem key={location} value={location}>{location}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Coach</InputLabel>
                    <Select
                      value={coachFilter}
                      onChange={(e) => setCoachFilter(e.target.value)}
                      label="Coach"
                    >
                      <MenuItem value="">All Coaches</MenuItem>
                      {uniqueCoaches.map(coach => (
                        <MenuItem key={coach} value={coach}>{coach}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={programmeTypeFilter}
                      onChange={(e) => setProgrammeTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Regular">Regular</MenuItem>
                      <MenuItem value="Max">Max</MenuItem>
                      <MenuItem value="Promo">Promo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={1}>
                  <Box display="flex" gap={1}>
                    <Badge badgeContent={activeFilterCount} color="primary">
                      <IconButton
                        onClick={handleClearFilters}
                        disabled={activeFilterCount === 0}
                        color="primary"
                      >
                        <Clear />
                      </IconButton>
                    </Badge>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 0 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                    <CustomLoader />
                  </Box>
                ) : error ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {error}
                  </Alert>
                ) : (
                  <DataGrid
                    rows={filteredClients}
                    columns={columns}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        py: 2,
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                      },
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        },
                      },
                      '& .MuiDataGrid-footerContainer': {
                        borderTop: `2px solid ${theme.palette.divider}`,
                      },
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};